import React, { useState } from "react";
import { Box, Typography, Chip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import configData from "config/config.json";

const useStyles = makeStyles((theme) => ({
  userItem: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    marginBottom: "8px",
    border: "1px solid #e0e0e0",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
      borderColor: "#1976d2",
    },
  },
  userItemClickable: {
    cursor: "pointer",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
      borderColor: "#1976d2",
      backgroundColor: "#f8f9fa",
    },
  },
  userItemHighlighted: {
    backgroundColor: "#fff3e0",
    border: "2px solid #ff9800",
    boxShadow: "0 0 20px rgba(255, 152, 0, 0.3)",
    animation: "$pulse 2s ease-in-out 3",
  },
  "@keyframes pulse": {
    "0%": {
      boxShadow: "0 0 20px rgba(255, 152, 0, 0.3)",
    },
    "50%": {
      boxShadow: "0 0 30px rgba(255, 152, 0, 0.5)",
    },
    "100%": {
      boxShadow: "0 0 20px rgba(255, 152, 0, 0.3)",
    },
  },
  badgeImage: {
    width: "40px",
    height: "40px",
    borderRadius: "4px",
    marginRight: "12px",
    flexShrink: 0,
    objectFit: "contain",
  },
  rankBadge: {
    minWidth: "40px",
    height: "40px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "1rem",
    marginRight: "16px",
    flexShrink: 0,
    backgroundColor: "#f5f5f5",
    color: "#666",
  },
  userInfo: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  teamName: {
    fontWeight: 600,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
  },
  userId: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontFamily: "monospace",
  },
  countryFlag: {
    width: "28px",
    height: "20px",
    borderRadius: "2px",
    marginRight: "8px",
    flexShrink: 0,
    objectFit: "cover",
  },
  countryFlagEmoji: {
    fontSize: "1.5rem",
    marginRight: "8px",
  },
  xpChip: {
    fontWeight: 600,
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    border: "1px solid #1976d2",
  },
  botChip: {
    backgroundColor: "#f3e5f5",
    color: "#7b1fa2",
    border: "1px solid #9c27b0",
  },
  humanChip: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    border: "1px solid #4caf50",
  },
}));

const getCountryFlag = (countryCode: any) => {
  if (!countryCode) return "🏳️";
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

const UserListItem = React.forwardRef(({ user, isHighlighted = false }: any, ref) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [countryImageError, setCountryImageError] = useState(false);

  const handleClick = () => {
    // Only navigate if user is human (not a bot)
    if (!user.isBot) {
      navigate(`${configData.routes.allUsers.userInfo}?id=${user.userId}`);
    }
  };

  const isClickable = !user.isBot;

  return (
    <Box
      ref={ref}
      className={`${styles.userItem} ${isClickable ? styles.userItemClickable : ""} ${
        isHighlighted ? styles.userItemHighlighted : ""
      }`}
      onClick={handleClick}
    >
      {/* Rank Badge */}
      <Box className={styles.rankBadge}>
        {user.position}
      </Box>

      {/* Badge Image */}
      {user.badgeUrl && (
        <img
          src={user.badgeUrl}
          alt="Badge"
          className={styles.badgeImage}
          onError={(e: any) => {
            e.target.style.display = "none";
          }}
        />
      )}

      {/* User Info */}
      <Box className={styles.userInfo}>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography className={styles.teamName}>
              {user.teamAutoName}
            </Typography>
            {/* Country Flag */}
            {user.imageUrl && !countryImageError ? (
              <img
                src={user.imageUrl}
                alt="Country"
                className={styles.countryFlag}
                onError={() => setCountryImageError(true)}
              />
            ) : (
              <span className={styles.countryFlagEmoji}>
                {getCountryFlag(user.countryCode)}
              </span>
            )}
          </Box>
          <Typography className={styles.userId}>
            ID: {user.userId}
          </Typography>
        </Box>

        <Box display="flex" gap={1} alignItems="center">
          <Chip
            size="small"
            icon={user.isBot ? <SmartToyIcon /> : <PersonIcon />}
            label={user.isBot ? "Bot" : "Human"}
            className={user.isBot ? styles.botChip : styles.humanChip}
          />
          <Chip
            size="small"
            label={`${user.xp?.toLocaleString() || user.xp} XP`}
            className={styles.xpChip}
          />
        </Box>
      </Box>
    </Box>
  );
});

UserListItem.displayName = "UserListItem";

export default UserListItem;
